export const formatActivityCount = (value) => {
    const number = Number(value);

    if (!Number.isFinite(number)) {
        return '0';
    }

    return number.toLocaleString();
};

export const ACTIVITY_CATEGORIES = [
    { value: 'ALL', label: 'All', icon: 'bi-grid', theme: 'slate' },
    { value: 'CLINIC_ENGAGEMENT', label: 'Clinic Engagement', icon: 'bi-building', theme: 'primary' },
    { value: 'REMINDER', label: 'Reminder', icon: 'bi-bell', theme: 'orange' },
    { value: 'RECALL', label: 'Recall', icon: 'bi-arrow-repeat', theme: 'purple' },
    { value: 'LAB', label: 'Lab', icon: 'bi-eyedropper', theme: 'cyan' },
    { value: 'SUMMARY', label: 'Summary', icon: 'bi-clipboard-check', theme: 'indigo' },
    { value: 'WELCOME', label: 'Welcome', icon: 'bi-emoji-smile', theme: 'success' },
    { value: 'PATIENT_ENGAGEMENT', label: 'Patient Engagement', icon: 'bi-people', theme: 'pink' },
    { value: 'POST_EXAM', label: 'Post Exam', icon: 'bi-eyeglasses', theme: 'violet' },
    { value: 'CONFIRMATION', label: 'Confirmation', icon: 'bi-check-circle', theme: 'teal' },
];

export const ACTIVITY_MEDIUM_TYPES = [
    { value: 'ALL', label: 'All', icon: 'bi-grid' },
    { value: 'EMAIL', label: 'Email', icon: 'bi-envelope-fill' },
    { value: 'SMS', label: 'SMS', icon: 'bi-chat-text-fill' },
    { value: 'CALL', label: 'Call', icon: 'bi-telephone-fill' },
];

export const ACTIVITY_STATUSES = [
    { value: 'ALL', label: 'All', icon: 'bi-circle' },
    { value: 'SENT', label: 'Sent', icon: 'bi-check-circle-fill' },
    { value: 'FAILED', label: 'Failed', icon: 'bi-x-circle-fill' },
    { value: 'QUEUED', label: 'Queued', icon: 'bi-hourglass-split' },
    { value: 'WAITING', label: 'Waiting', icon: 'bi-clock-history' },
    { value: 'RESEND', label: 'Resend', icon: 'bi-arrow-repeat' },
];

export const ACTIVITY_SORT_FIELDS = [
    { value: 'createdAt', label: 'Created date' },
    { value: 'modifiedAt', label: 'Modified date' },
    { value: 'firstName', label: 'First name' },
    { value: 'lastName', label: 'Last name' },
    { value: 'category', label: 'Category' },
    { value: 'activityName', label: 'Activity name' },
    { value: 'status', label: 'Status' },
    { value: 'mediumType', label: 'Medium' },
];

export const DEFAULT_ACTIVITY_FILTERS = {
    firstName: '',
    lastName: '',
    preferredName: '',
    patientName: '',
    category: 'ALL',
    mediumType: 'ALL',
    status: 'ALL',
    activityName: '',
    receiver: '',
    date: '',
    dateFrom: '',
    dateTo: '',
};

export const DEFAULT_ACTIVITY_SORT = {
    sortBy: 'createdAt',
    sortOrder: 'DESC',
};

const appendTextFilter = (parts, field, value, wildcard = true) => {
    const trimmed = value?.trim();
    if (!trimmed) {
        return;
    }
    parts.push(`${field}==${wildcard ? `${trimmed}*` : trimmed}`);
};

export const buildActivityQuery = (filters, sort = DEFAULT_ACTIVITY_SORT) => {
    const parts = [];

    appendTextFilter(parts, 'firstName', filters.firstName);
    appendTextFilter(parts, 'lastName', filters.lastName);
    appendTextFilter(parts, 'preferredName', filters.preferredName);
    appendTextFilter(parts, 'patientName', filters.patientName);
    appendTextFilter(parts, 'activityName', filters.activityName);
    appendTextFilter(parts, 'receiver', filters.receiver);

    if (filters.category && filters.category !== 'ALL') {
        parts.push(`category==${filters.category}`);
    }

    if (filters.mediumType && filters.mediumType !== 'ALL') {
        parts.push(`mediumType==${filters.mediumType}`);
    }

    if (filters.status && filters.status !== 'ALL') {
        parts.push(`status==${filters.status}`);
    }

    if (filters.date) {
        parts.push(`date==${filters.date}`);
    } else {
        if (filters.dateFrom) {
            parts.push(`createdAt=ged=${filters.dateFrom}`);
        }
        if (filters.dateTo) {
            parts.push(`createdAt=led=${filters.dateTo}`);
        }
    }

    if (sort?.sortBy) {
        parts.push(`sortBy==${sort.sortBy}`);
    }

    if (sort?.sortOrder) {
        parts.push(`sortOrder==${sort.sortOrder}`);
    }

    return parts.join(',');
};

export const countActiveFilters = (filters) => {
    let count = 0;

    if (filters.firstName?.trim()) count += 1;
    if (filters.lastName?.trim()) count += 1;
    if (filters.preferredName?.trim()) count += 1;
    if (filters.patientName?.trim()) count += 1;
    if (filters.activityName?.trim()) count += 1;
    if (filters.receiver?.trim()) count += 1;
    if (filters.category && filters.category !== 'ALL') count += 1;
    if (filters.mediumType && filters.mediumType !== 'ALL') count += 1;
    if (filters.status && filters.status !== 'ALL') count += 1;
    if (filters.date) count += 1;
    if (!filters.date && filters.dateFrom) count += 1;
    if (!filters.date && filters.dateTo) count += 1;

    return count;
};

const formatCategoryLabel = (category) => {
    if (!category || category === 'ALL') {
        return 'All categories';
    }

    return category
        .toLowerCase()
        .split('_')
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
};

export const getActiveFilterChips = (filters) => {
    const chips = [];

    const addChip = (field, label, clearValue = '') => {
        chips.push({ field, label, clearValue });
    };

    if (filters.firstName?.trim()) {
        addChip('firstName', `First: ${filters.firstName.trim()}`);
    }
    if (filters.lastName?.trim()) {
        addChip('lastName', `Last: ${filters.lastName.trim()}`);
    }
    if (filters.preferredName?.trim()) {
        addChip('preferredName', `Preferred: ${filters.preferredName.trim()}`);
    }
    if (filters.patientName?.trim()) {
        addChip('patientName', `Patient: ${filters.patientName.trim()}`);
    }
    if (filters.activityName?.trim()) {
        addChip('activityName', `Activity: ${filters.activityName.trim()}`);
    }
    if (filters.receiver?.trim()) {
        addChip('receiver', `Receiver: ${filters.receiver.trim()}`);
    }
    if (filters.category && filters.category !== 'ALL') {
        addChip('category', formatCategoryLabel(filters.category), 'ALL');
    }
    if (filters.mediumType && filters.mediumType !== 'ALL') {
        addChip('mediumType', filters.mediumType, 'ALL');
    }
    if (filters.status && filters.status !== 'ALL') {
        addChip('status', filters.status, 'ALL');
    }
    if (filters.date) {
        addChip('date', `Date: ${filters.date}`);
    }
    if (!filters.date && filters.dateFrom) {
        addChip('dateFrom', `From: ${filters.dateFrom}`);
    }
    if (!filters.date && filters.dateTo) {
        addChip('dateTo', `To: ${filters.dateTo}`);
    }

    return chips;
};
